import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Download, Link2, Search } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDate, fmtDateTime } from "@/lib/intl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { downloadFile, errMsg, fmtKpi } from "./kpiFormat";
import { LEAD_DRILLS, SamePersonDialog } from "./SamePerson";

const ClientCell = ({ r }) => {
  const label = r.client_name || r.client_email || r.client_id;
  return (
    <div className="min-w-[160px]">
      {r.client_email ? (
        <Link to={`/clients/${encodeURIComponent(r.client_email)}`} className="font-medium text-zinc-900 hover:text-blue-700 hover:underline"
          data-testid={`kpi-drill-client-link-${r.client_email}`}>{label}</Link>
      ) : <span className="font-medium text-zinc-900">{label}</span>}
      {r.client_name && r.client_email && <div className="text-2xs text-zinc-500">{r.client_email}</div>}
    </div>
  );
};

const value = (r, c) => {
  const v = r[c.key];
  if (c.type === "client") return <ClientCell r={r} />;
  if (v === null || v === undefined || v === "") return <span className="text-zinc-300">—</span>;
  if (c.type === "bool") return <span className={v ? "text-emerald-700 font-medium" : "text-zinc-500"}>{v ? "Yes" : "No"}</span>;
  if (c.type === "money") return <span className="font-mono-num">{fmtKpi(v, "money2")}</span>;
  if (c.type === "datetime") return <span className="font-mono-num whitespace-nowrap">{fmtDateTime(v)}</span>;
  if (c.type === "date") return <span className="font-mono-num whitespace-nowrap">{fmtDate(v)}</span>;
  return String(v);
};

const searchText = (r) => [r.client_name, r.client_email, r.client_id, r.product, r.service, r.channel, r.practitioner, r.form, r.campaign]
  .filter(Boolean).join(" ").toLowerCase();

export const DrillDialog = ({ week, kpi, onClose, onChanged }) => {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [q, setQ] = useState(kpi.search || "");
  const [link, setLink] = useState(null);
  const [reload, setReload] = useState(0);
  const canLink = LEAD_DRILLS.has(kpi.id) && !kpi.service && ["owner", "manager"].includes(user?.role);
  const params = useMemo(() => (kpi.service ? { service: kpi.service.id } : {}), [kpi.service]);
  useEffect(() => {
    setD(null);
    api.get(`/kpi/reports/${week}/drill/${kpi.id}`, { params, timeout: 120000 }).then((r) => setD(r.data))
      .catch((e) => { toast.error(errMsg(e)); onClose(); });
  }, [week, kpi.id, params, onClose, reload]);
  const rows = useMemo(() => (d?.rows || []).filter((r) => !q || searchText(r).includes(q.toLowerCase())), [d, q]);
  const title = kpi.service ? `${kpi.label} — ${kpi.service.name}` : kpi.label;

  const csv = () => downloadFile(`/kpi/reports/${week}/drill/${kpi.id}/csv`, params, `${title} - week ${week}.csv`.replace(/\//g, "-"))
    .catch((e) => toast.error(errMsg(e)));

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[88vh] flex flex-col" data-testid="kpi-drill-dialog">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-baseline gap-3" data-testid="kpi-drill-title">
            {title}
            {d && <span className="font-mono-num text-lg text-zinc-500" data-testid="kpi-drill-value">{d.value === null || d.value === undefined ? "N/A" : fmtKpi(d.value, kpi.unit)}</span>}
          </DialogTitle>
          <DialogDescription data-testid="kpi-drill-note">{d?.note || "Loading the breakdown…"}</DialogDescription>
        </DialogHeader>
        {d?.source === "live" && (
          <div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900" data-testid="kpi-drill-live-warning">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            This report was calculated before breakdowns were saved, so this list is recalculated from today's data and may not
            add up exactly. Refresh the draft (or request a correction on an approved week) to save a matching list.
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="relative w-72">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, product…" className="h-9 pl-8"
              data-testid="kpi-drill-search" />
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-zinc-500" data-testid="kpi-drill-count">
              {d ? `${rows.length}${q ? ` of ${d.rows.length}` : ""} row(s)` : ""}
              {d?.source === "snapshot" && ` · saved with version ${d.version}`}
            </span>
            <Button size="sm" variant="outline" onClick={csv} disabled={!d?.rows.length} data-testid="kpi-drill-csv-btn">
              <Download className="h-4 w-4 mr-1.5" />Download CSV
            </Button>
          </div>
        </div>
        <div className="flex-1 overflow-auto rounded-md border border-zinc-200">
          {!d ? <div className="h-48 animate-pulse bg-zinc-50" /> : d.rows.length === 0 ? (
            <p className="p-6 text-sm text-zinc-500" data-testid="kpi-drill-empty">Nothing is counted in this figure for this week.</p>
          ) : (
            <table className="w-full text-sm" data-testid="kpi-drill-table">
              <thead className="sticky top-0 bg-zinc-50">
                <tr className="text-left text-2xs uppercase tracking-[0.16em] text-zinc-500">
                  {d.columns.map((c) => <th key={c.key} className="px-3 py-2 font-semibold">{c.label}</th>)}
                  {canLink && <th className="px-3 py-2" />}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={d.columns.length + (canLink ? 1 : 0)} className="px-3 py-6 text-center text-sm text-zinc-500" data-testid="kpi-drill-no-match">
                    No rows match “{q}”</td></tr>
                )}
                {rows.map((r, i) => (
                  <tr key={i} className="group border-t border-zinc-100 align-top" data-testid={`kpi-drill-row-${i}`}>
                    {d.columns.map((c) => <td key={c.key} className="px-3 py-2">{value(r, c)}</td>)}
                    {canLink && (
                      <td className="px-3 py-2 text-right">
                        <button type="button" onClick={() => setLink(r)} data-testid={`kpi-drill-same-person-${i}`}
                          className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-zinc-500 transition-colors hover:text-blue-700">
                          <Link2 className="h-3.5 w-3.5" />Same person…
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {link && (
          <SamePersonDialog week={week} row={link} rows={d?.rows || []} onClose={() => setLink(null)}
            onLinked={() => { setLink(null); setReload((n) => n + 1); onChanged?.(); }} />
        )}
      </DialogContent>
    </Dialog>
  );
};
